import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { registerUserAction, googleLoginAction } from "../../../redux/slices/users/usersSlice";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import LoadingComponent from "../../LoadingComp/LoadingComponent";

const RegisterForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    password: "",
    confirmPassword: "", // ADD THIS
  });
  
  const [passwordError, setPasswordError] = useState(""); // ADD THIS
  
  const { fullname, email, password, confirmPassword } = formData;
  
  const onChangeHandler = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear password error when user types
    if (passwordError) setPasswordError("");
  };
  
  const onSubmitHandler = (e) => {
    e.preventDefault();
    
    // VALIDATE PASSWORDS MATCH
    if (password !== confirmPassword) {
      setPasswordError(t("register.passwordsDoNotMatch"));
      return;
    }
    
    if (password.length < 6) {
      setPasswordError(t("register.passwordTooShort"));
      return;
    }
    
    dispatch(registerUserAction({ fullname, email, password }));
  };
  
  const handleGoogleLogin = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/api/users/auth/google`;
  };
  
  const { user, error, loading } = useSelector((state) => state?.users);
  
  useEffect(() => {
    if (user) {
      window.location.href = "/login";
    }
  }, [user]);
  
  return (
    <>
      <section className="relative overflow-x-hidden">
        <div className="container px-4 mx-auto">
          <div className="flex flex-wrap items-center">
            <div className="w-full lg:w-2/6 px-4 mb-12 lg:mb-0">
              <div className="py-20 text-center">
                <h3 className="mb-8 text-4xl md:text-5xl font-bold font-heading">
                  {t("register.signupWithSocial")}
                </h3>
                
                {error && <ErrorMsg message={error?.message} />}
                
                {/* Google Sign Up Button */}
                <button
                  onClick={handleGoogleLogin}
                  className="w-full mb-4 flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {t("register.googleSignUp")}
                </button>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">
                      {t("register.or")}
                    </span>
                  </div>
                </div>
                
                <form onSubmit={onSubmitHandler}>
                  <input
                    name="fullname"
                    value={fullname}
                    onChange={onChangeHandler}
                    className="w-full mb-4 px-12 py-6 border border-gray-200 focus:ring-blue-300 focus:border-blue-300 rounded-md"
                    type="text"
                    placeholder={t("register.fullName")}
                    required
                  />
                  <input
                    name="email"
                    value={email}
                    onChange={onChangeHandler}
                    className="w-full mb-4 px-12 py-6 border border-gray-200 focus:ring-blue-300 focus:border-blue-300 rounded-md"
                    type="email"
                    placeholder={t("register.email")}
                    required
                  />
                  <input
                    name="password"
                    value={password}
                    onChange={onChangeHandler}
                    className="w-full mb-4 px-12 py-6 border border-gray-200 focus:ring-blue-300 focus:border-blue-300 rounded-md"
                    type="password"
                    placeholder={t("register.password")}
                    required
                  />
                  {/* ADD CONFIRM PASSWORD FIELD */}
                  <input
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={onChangeHandler}
                    className={`w-full mb-2 px-12 py-6 border focus:ring-blue-300 focus:border-blue-300 rounded-md ${
                      passwordError ? "border-red-500" : "border-gray-200"
                    }`}
                    type="password"
                    placeholder={t("register.confirmPassword")}
                    required
                  />
                  
                  {/* SHOW PASSWORD ERROR MESSAGE */}
                  {passwordError && (
                    <p className="text-red-500 text-sm text-left mb-4">
                      {passwordError}
                    </p>
                  )}
                  
                  {loading ? (
                    <LoadingComponent />
                  ) : (
                    <button 
                      type="submit"
                      className="mt-12 md:mt-16 bg-black hover:bg-gray-600 text-white font-bold font-heading py-5 px-8 rounded-md uppercase w-full"
                    >
                      {t("register.registerButton")}
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="hidden lg:block lg:absolute top-0 bottom-0 right-0 lg:w-3/6 bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage:
              'url("https://res.cloudinary.com/dc6xthiol/image/upload/v1774647717/register_divg2g.png")',
          }}
        />
      </section>
    </>
  );
};

export default RegisterForm;